import {
	deleteConnections,
	EgnyteConnectionListItemModel,
	list
} from "client/Api/Egnyte/egnyteConfiguration";
import {
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { resolveFormattedTextString } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../../SettingsPage";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { useEgnyteConnectionDialog } from "./EgnyteConnectionDialog";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const EgnyteConnectionPage = (): ReactElement => {

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<readonly EgnyteConnectionListItemModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<readonly EgnyteConnectionListItemModel[]>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [sortingState, setSortingState] = useState<DataSourceSortableState<EgnyteConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);

	const tableRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	useActiveSettingsMenuItem(SettingsPageKind.Egnyte);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [egnyteConnectionDialog, openEgnyteConnectionDialog] = useEgnyteConnectionDialog();

	const { sortingFor } = useSortHandler(sortingState, setSortingState);

	const { currentItems, pagination } = useDataSource(
		allItems,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 25
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: false
		}
	);

	const createHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<EgnyteConnectionListItemModel> = await openEgnyteConnectionDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			setAllItems(
				x => [...x, result.result]
			);
		},
		[openEgnyteConnectionDialog]
	);

	const deleteHandler = useCallback(
		async (items: readonly EgnyteConnectionListItemModel[]): Promise<void> => {
			const result = await openConfirmationDialog({
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisConnection")
					: localize("_AreYouSureYouWantToDeleteTheseConnections"),
				confirmText: localize("_Delete"),
				trackingId: "delete-egnyte-connection-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteConnections(
					items.map(i => i.id),
					globalCancellation
				);

				setAllItems(
					x => x.filter(
						i => !items.some(di => i.id === di.id)
					)
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ErrorDeletingConnections"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const editHandler = useCallback(
		async (item: EgnyteConnectionListItemModel): Promise<void> => {
			const result: TryResult<EgnyteConnectionListItemModel> = await openEgnyteConnectionDialog({
				connectionId: item.id
			});

			if (!result.wasSuccessful) {
				return;
			}

			setAllItems(
				x => {
					const copy = [...x];

					const index: number = copy.findIndex(x => x.id === result.result.id);
					if (index === -1) {
						console.warn(`Edited an non existing item ${result.result.id}`);
						copy.push(result.result);
					} else {
						copy.splice(index, 1, result.result);
					}

					return copy;
				}
			);
		},
		[openEgnyteConnectionDialog]
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => {
			return {
				selection: selectedItems,
				allItems,
				isMobile
			};
		},
		[
			allItems,
			isMobile,
			selectedItems
		]
	);

	const selectionActions: readonly ConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly ConnectionAction[] = useMemo(
		() => listContextualActions({
			edit: editHandler,
			delete: deleteHandler
		}),
		[deleteHandler, editHandler]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ConnectionActionContext> => {
			return getMainAction(createHandler);
		},
		[createHandler]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {

				setIsBusy(true);
				try {
					const connections: readonly EgnyteConnectionListItemModel[] = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(connections);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				searchTerm,
				sortingState,
				currentPageNumber
			);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			currentPageNumber,
			searchTerm,
			sortingState
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(searchTerm, sortingState);
			setCurrentPageNumber(0);
		},
		[searchTerm, sortingState]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Egnyte")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Egnyte")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				title={localize("_EgnyteConnection")}
				hasSelection={hasSelection}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInputForm
							onChange={setSearchTerm}
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name"),
										localize("_DomainName")
									]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								},
								{
									title: localize("_DomainName"),
									sortable: sortingFor("domainName"),
									trackingId: "domain-name"
								}
							]}
							trackingId="egnyte-connections"
							ref={tableRef}
							noResultConfig={noResultConfig}
						>
							{item => (
								<TableView.Row
									selectable={selectionFor(item)}
									trackingId={item.id.toString()}
									onExecute={() => editHandler(item)}
									onContextMenu={contextMenuHandler}
								>
									<TableView.Cell>{item.name}</TableView.Cell>
									<TableView.Cell>{item.domainName}</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>
						<Pagination
							{...pagination}
							onPageNumberChanged={setCurrentPageNumber}
						/>

						{contextMenuProps && (
							<ContextMenu
								{...contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="egnyte-connection-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{confirmationDialog}
			{egnyteConnectionDialog}
		</>
	);
};
EgnyteConnectionPage.displayName = "EgnyteConnectionPage";

function searchMatchFunction(
	item: EgnyteConnectionListItemModel,
	searchTerm: string
): boolean {
	const {
		name,
		domainName
	} = item;

	return contains(name, searchTerm)
		|| contains(domainName, searchTerm);
}

const useDataSource = createLocalDataSource<EgnyteConnectionListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true
	})
	.getHook();


type ConnectionActionContext = {
	selection: readonly EgnyteConnectionListItemModel[];
	allItems: readonly EgnyteConnectionListItemModel[];
	isMobile: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

type ContextualActionsHandlers = {
	edit(item: EgnyteConnectionListItemModel): void;
	delete(items: readonly EgnyteConnectionListItemModel[]): void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => handlers.edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => handlers.delete(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: (c) => c.allItems.length === 0,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

export const egnyteConfigurationRouting = {
	page: "/EgnyteConfiguration"
};